import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../config/region';
import DashboardNavBar from './DashboardNavBar';
import { dtLocale } from '../../utils/dateLocale';


// ─── constants ───────────────────────────────────────────────────────────────

const CDN_RESIZE = 'https://media.honestlee.app/r';

// ─── helpers ─────────────────────────────────────────────────────────────────

function getAuthToken() {
  return (
    localStorage.getItem('venueapp_token') ||
    localStorage.getItem('venue_app_token') ||
    localStorage.getItem('token') ||
    null
  );
}

/**
 * Build a Lambda@Edge resize URL from an s3_key or full CDN URL.
 * @param {string|null} s3Key
 * @param {{ w?: number, h?: number, q?: number, fit?: string }} opts
 * @returns {string|null}
 */
function getResizeUrl(s3Key, { w, h, q = 75, fit } = {}) {
  if (!s3Key) return null;
  // Strip CDN/CloudFront prefix if a full URL was passed
  const key = s3Key.startsWith('http')
    ? (s3Key.match(/(?:media\.honestlee\.app|cloudfront\.net)\/(.+?)(?:\?|$)/)?.[1] || s3Key)
    : s3Key;
  let url = `${CDN_RESIZE}?key=${encodeURIComponent(key)}&f=webp&q=${q}`;
  if (w) url += `&w=${w}`;
  if (h) url += `&h=${h}`;
  if (fit) url += `&fit=${fit}`;
  return url;
}

/** Resolve the best available key for a photo object. */
function resolveKey(photo) {
  return photo.s3_key || photo.cloudfront_url || photo.thumbnail_url || null;
}

/** Format a date string as "12 Jan 2025" or fall back gracefully. */
function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(dtLocale(), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
}

// ─── sub-components ──────────────────────────────────────────────────────────

function Spinner({ size = 40, color = '#f97316' }) {
  return (
    <>
      <div
        style={{
          width: size,
          height: size,
          border: `4px solid rgba(0,0,0,0.08)`,
          borderTop: `4px solid ${color}`,
          borderRadius: '50%',
          animation: 'vdp-spin 0.9s linear infinite',
          margin: '0 auto',
        }}
      />
      <style>{`@keyframes vdp-spin{0%{transform:rotate(0deg);}100%{transform:rotate(360deg);}}`}</style>
    </>
  );
}

// ─── lightbox ────────────────────────────────────────────────────────────────

function Lightbox({ photos, initialIndex, onClose }) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);
  const photo = photos[index];
  const key = resolveKey(photo);
  const imgUrl = getResizeUrl(key, { w: 1200, h: 1200, q: 85, fit: 'inside' });
  const dateLabel = formatDate(photo.taken_at || photo.created_at);
  const total = photos.length;

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, total - 1));
      if (e.key === 'ArrowLeft')  setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, total]);

  // Update index when prop changes (e.g. re-open)
  useEffect(() => { setIndex(initialIndex); }, [initialIndex]);

  const goLeft  = (e) => { e.stopPropagation(); setIndex((i) => Math.max(i - 1, 0)); };
  const goRight = (e) => { e.stopPropagation(); setIndex((i) => Math.min(i + 1, total - 1)); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('vd.photoLightbox')}
      style={lb.overlay}
      onClick={onClose}
    >
      {/* Image wrapper — stops click propagation so tapping the image itself
          doesn't close the lightbox; only the background does */}
      <div style={lb.imgWrap} onClick={(e) => e.stopPropagation()}>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={t('vd.photoXOfY', { index: index + 1, total })}
            style={lb.img}
            draggable={false}
          />
        ) : (
          <div style={lb.noImg}>{t('vd.imageUnavailable')}</div>
        )}
      </div>

      {/* Arrows */}
      {total > 1 && index > 0 && (
        <button
          style={{ ...lb.arrowBtn, left: 12 }}
          onClick={goLeft}
          aria-label={t('vd.previousPhoto')}
        >
          ‹
        </button>
      )}
      {total > 1 && index < total - 1 && (
        <button
          style={{ ...lb.arrowBtn, right: 12 }}
          onClick={goRight}
          aria-label={t('vd.nextPhoto')}
        >
          ›
        </button>
      )}

      {/* Bottom bar: date + counter */}
      <div style={lb.bottomBar} onClick={(e) => e.stopPropagation()}>
        {dateLabel && (
          <span style={lb.dateLabel}>{dateLabel}</span>
        )}
        <span style={lb.counter}>{index + 1} / {total}</span>
      </div>

      {/* Close button */}
      <button
        style={lb.closeBtn}
        onClick={onClose}
        aria-label={t('vd.closeLightbox')}
      >
        ✕
      </button>
    </div>
  );
}

const lb = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 9999,
    background: 'rgba(0,0,0,0.93)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  imgWrap: {
    maxWidth: 'min(92vw, 900px)',
    maxHeight: 'min(80vh, 800px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'default',
  },
  img: {
    maxWidth: '100%',
    maxHeight: 'min(80vh, 800px)',
    objectFit: 'contain',
    borderRadius: 8,
    display: 'block',
    userSelect: 'none',
    WebkitUserDrag: 'none',
  },
  noImg: {
    color: '#9ca3af',
    fontSize: 15,
    fontStyle: 'italic',
  },
  arrowBtn: {
    position: 'fixed',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)',
    border: 'none',
    color: 'white',
    fontSize: 42,
    lineHeight: 1,
    width: 52,
    height: 64,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    userSelect: 'none',
  },
  bottomBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
    cursor: 'default',
    zIndex: 10000,
  },
  dateLabel: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontWeight: 500,
  },
  counter: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 12,
    fontWeight: 500,
    marginLeft: 'auto',
    paddingLeft: 12,
  },
  closeBtn: {
    position: 'fixed',
    top: 14,
    right: 16,
    background: 'rgba(255,255,255,0.15)',
    border: 'none',
    color: 'white',
    fontSize: 18,
    width: 38,
    height: 38,
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
};

// ─── masonry column gallery ───────────────────────────────────────────────────

/**
 * Renders photos in a CSS `columns` masonry layout.
 * Uses `rowH300` URL variant — natural width, h=300 constraint.
 */
function MasonryGallery({ photos, onPhotoClick }) {
  const { t } = useTranslation();
  // Responsive columns via a ResizeObserver on the container
  const containerRef = useRef(null);
  const [cols, setCols] = useState(2);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry.contentRect.width;
      setCols(w >= 600 ? 3 : 2);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        columns: cols,
        columnGap: 8,
        padding: '12px 12px 24px',
      }}
    >
      {photos.map((photo, i) => {
        const key = resolveKey(photo);
        const src = getResizeUrl(key, { h: 300, q: 75, fit: 'inside' });
        if (!src) return null;
        return (
          <img
            key={photo.id || i}
            src={src}
            alt={t('vd.venuePhotoN', { n: i + 1 })}
            loading="lazy"
            onClick={() => onPhotoClick(i)}
            style={{
              width: '100%',
              marginBottom: 8,
              display: 'block',
              borderRadius: 8,
              cursor: 'pointer',
              breakInside: 'avoid',
              WebkitColumnBreakInside: 'avoid',
              background: '#f3f4f6', // placeholder while loading
            }}
          />
        );
      })}
    </div>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

const VenueDashboardPhotosPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [pageState, setPageState] = useState('loading'); // loading | ready | error
  const [errorType, setErrorType] = useState(null);      // 401 | 403 | 404 | 'generic'
  const [photos, setPhotos] = useState([]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const fetchPhotos = useCallback(async () => {
    setPageState('loading');
    setErrorType(null);

    const token = getAuthToken();
    if (!token) {
      setPageState('error');
      setErrorType(401);
      return;
    }

    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/venue-dashboard/${encodeURIComponent(slug)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 401) { setPageState('error'); setErrorType(401); return; }
      if (res.status === 403) { setPageState('error'); setErrorType(403); return; }
      if (res.status === 404) { setPageState('error'); setErrorType(404); return; }

      const data = await res.json();

      if (!data.success) {
        setPageState('error');
        setErrorType('generic');
        return;
      }

      if (!data.myRole) {
        setPageState('error');
        setErrorType(403);
        return;
      }

      setPhotos(data.photos || []);
      setPageState('ready');
    } catch (err) {
      console.error('[VenueDashboardPhotosPage] fetch error:', err);
      setPageState('error');
      setErrorType('generic');
    }
  }, [slug]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  // ── header (shared across all states) ──────────────────────────────────────
  const Header = ({ showCount = false }) => (
    <div style={s.header}>
      <button
        style={s.backBtn}
        onClick={() => navigate(-1)}
        aria-label={t('vd.goBack')}
      >
        ←
      </button>
      <span style={s.headerTitle}>{t('vd.photos')}</span>
      <div style={s.headerRight}>
        {showCount && photos.length > 0 && (
          <span style={s.countBadge}>
            {t('vd.photosCount', { count: photos.length })}
          </span>
        )}
      </div>
    </div>
  );

  // ── loading ─────────────────────────────────────────────────────────────────
  if (pageState === 'loading') {
    return (
      <div style={s.screen}>
        <Header />
        <div style={s.centred}>
          <Spinner />
          <p style={s.subText}>{t('vd.loadingPhotos')}</p>
        </div>
      </div>
    );
  }

  // ── error ───────────────────────────────────────────────────────────────────
  if (pageState === 'error') {
    let icon = '⚠️';
    let heading = t('vd.somethingWentWrong');
    let sub = t('vd.pleaseTryAgainLater');

    if (errorType === 401) {
      icon = '🔒';
      heading = t('vd.pleaseLogIn');
      sub = t('vd.needLoggedInToViewPage');
    } else if (errorType === 403) {
      icon = '🚫';
      heading = t('vd.accessDenied');
      sub = t('vd.noAccessToThisVenue');
    } else if (errorType === 404) {
      icon = '🔍';
      heading = t('vd.venueNotFound');
      sub = t('vd.venueNotExistOrLinkInvalid');
    }

    return (
      <div style={s.screen}>
        <Header />
        <div style={s.centred}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{icon}</div>
          <h2 style={s.errorHeading}>{heading}</h2>
          <p style={s.subText}>{sub}</p>
          {errorType === 401 && (
            <button style={s.primaryBtn} onClick={() => navigate('/login')}>
              {t('vd.logIn')}
            </button>
          )}
          <button
            style={{ ...s.primaryBtn, background: '#6b7280', marginTop: 8 }}
            onClick={() => navigate('/venue-dashboard')}
          >
            {t('vd.myVenues')}
          </button>
        </div>
      </div>
    );
  }

  // ── ready ───────────────────────────────────────────────────────────────────
  return (
    <div style={s.screen}>
      <Header showCount />

      {photos.length === 0 ? (
        // ── empty state ──────────────────────────────────────────────────────
        <div style={s.centred}>
          <div style={{ fontSize: 56, marginBottom: 14 }}>📷</div>
          <h2 style={{ ...s.errorHeading, fontSize: 18 }}>{t('vd.noApprovedPhotos')}</h2>
          <p style={{ ...s.subText, maxWidth: 280 }}>
            {t('vd.noApprovedPhotosDesc')}
          </p>
        </div>
      ) : (
        // ── gallery ──────────────────────────────────────────────────────────
        <div style={s.galleryWrapper}>
          <MasonryGallery photos={photos} onPhotoClick={openLightbox} />
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <Lightbox
          photos={photos}
          initialIndex={lightboxIndex}
          onClose={closeLightbox}
        />
      )}
      <DashboardNavBar />
    </div>
  );
};

// ─── style objects ────────────────────────────────────────────────────────────

const s = {
  screen: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    background: '#f9fafb',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    height: 64,
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 50%, #fbbf24 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    fontSize: 22,
    fontWeight: 700,
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: 8,
    lineHeight: 1,
    width: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    color: 'white',
    fontWeight: 700,
    fontSize: 17,
    flex: 1,
    textAlign: 'center',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    padding: '0 8px',
  },
  headerRight: {
    width: 80,
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
  },
  countBadge: {
    background: 'rgba(255,255,255,0.25)',
    color: 'white',
    fontSize: 12,
    fontWeight: 700,
    padding: '4px 10px',
    borderRadius: 20,
    whiteSpace: 'nowrap',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  },
  centred: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px 20px',
    textAlign: 'center',
    gap: 8,
  },
  errorHeading: {
    fontSize: 20,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
  },
  subText: {
    fontSize: 14,
    color: '#6b7280',
    margin: 0,
    lineHeight: 1.5,
  },
  primaryBtn: {
    display: 'block',
    width: '100%',
    maxWidth: 240,
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 8,
    boxShadow: '0 4px 12px rgba(249,115,22,0.3)',
  },
  galleryWrapper: {
    flex: 1,
    overflowY: 'auto',
  },
};

export default VenueDashboardPhotosPage;
